/**
 * Company settings shell — the person's Profile, then the Company's
 * sections, one rendered at a time, with the active one a URL place
 * (`?section=`, committed with `replace`) so a section is linkable and
 * survives Back. `SETTINGS_SECTIONS` in lib/settings-index is the one
 * registry a new section is added to, and `SECTION_BODIES` below is what
 * each one draws, held to it by its type. Scalar Company settings commit
 * per row through the partial company-settings PUT; template-shaped config
 * (including job sheet templates, #188) lives in the Documents & emails
 * studio.
 *
 * From md the section list is the sidebar's (AppNav's SettingsNav); below
 * md it is this page's drill-down, which is the whole screen until a
 * section is picked (DESIGN-SYSTEM.md, Admin settings page).
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { AdminPageHeader } from "../../components/ui/AdminPageHeader";
import { Banner } from "../../components/ui/Banner";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from "../../components/ui/icons";
import {
  getCompanySettings,
  saveCompanySettings,
  type CompanySettings,
  type CompanySettingsUpdate,
} from "../../lib/company-settings-api";
import { useOptionalAuth } from "../../lib/auth";
import { lastPlace } from "../../lib/last-place";
import {
  INTAKE_SETTINGS_URL,
  SETTINGS_GROUPS,
  SETTINGS_SECTIONS,
  type SettingsSectionId,
} from "../../lib/settings-index";
import { Loading } from "./section-chrome";
import { GeneralSection } from "./GeneralSection";
import { PaymentsSection } from "./PaymentsSection";
import { ProfileSection } from "./ProfileSection";
import { TemplatesSection } from "./TemplatesSection";
import { OperationsSection } from "./OperationsSection";
import { IntegrationsSection } from "./IntegrationsSection";
import styles from "./index.module.css";

/** Everything a settings-backed section may need from the shell. */
type SectionContext = {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
};

type SectionBody =
  | {
      /** Section reads the company-settings fetch; shell gates on Loading. */
      needsSettings: true;
      render: (ctx: SectionContext) => ReactNode;
    }
  | { needsSettings: false; render: () => ReactNode };

/** What each section draws, and whether it needs the settings row. */
const SECTION_BODIES: Record<SettingsSectionId, SectionBody> = {
  profile: { needsSettings: false, render: () => <ProfileSection /> },
  general: {
    needsSettings: true,
    render: (ctx) => (
      <GeneralSection settings={ctx.settings} onPatch={ctx.onPatch} />
    ),
  },
  templates: {
    needsSettings: true,
    render: (ctx) => (
      <TemplatesSection settings={ctx.settings} onPatch={ctx.onPatch} />
    ),
  },
  payments: {
    needsSettings: true,
    render: (ctx) => (
      <PaymentsSection settings={ctx.settings} onPatch={ctx.onPatch} />
    ),
  },
  operations: { needsSettings: false, render: () => <OperationsSection /> },
  integrations: { needsSettings: false, render: () => <IntegrationsSection /> },
};

const SECTIONS = SETTINGS_SECTIONS.map((s) => ({
  ...s,
  ...SECTION_BODIES[s.id],
}));

export default function SettingsPage() {
  const auth = useOptionalAuth();
  const logOut = () => {
    void (async () => {
      await auth?.signOut();
      window.location.assign("/login");
    })();
  };
  // No/unknown `?section=` shows General from md and the drill-down root
  // list below it; committed with `replace` so flipping sections doesn't
  // fill the Back history.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = SECTIONS.find((s) => s.id === searchParams.get("section"));
  // General, not Profile, when none is named: the Company's settings are
  // what most visits are for.
  const active = requested ?? SECTIONS.find((s) => s.id === "general")!;
  const mobileView = requested ? "section" : "list";

  const [loadError, setLoadError] = useState<string | null>(null);
  const [settings, setSettings] = useState<CompanySettings | null>(null);

  useEffect(() => {
    document.title = "Settings — Rental Admin";
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    getCompanySettings(ac.signal)
      .then((next) => {
        setSettings(next);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => ac.abort();
  }, []);

  /** Partial PUT; rows await this and render failures inline. */
  const onPatch = useCallback(async (patch: CompanySettingsUpdate) => {
    const next = await saveCompanySettings(patch);
    setSettings(next);
  }, []);

  const select = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", id);
    setSearchParams(next, { replace: true });
  };

  const backToList = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("section");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* From md the sidebar is Settings, with the way out at its top;
            below it this is the way back to the page the reader came from. */}
        <Link to={lastPlace()} className={styles.exit}>
          <ChevronLeftIcon className={styles.backIcon} />
          Back
        </Link>
        <AdminPageHeader
          title="Settings"
          subtitle="Your sign-in, and your company's documents, payments and operations"
        />

        {loadError && (
          <Banner
            variant="error"
            className={styles.loadBanner}
            onDismiss={() => setLoadError(null)}
          >
            {loadError}
          </Banner>
        )}

        <div className={styles.layout}>
          {/* Below md the section list is the whole screen; from md it is
              the sidebar's (AppNav), and this nav is not drawn. */}
          <nav
            aria-label="Settings sections"
            className={`${styles.nav} ${
              mobileView === "list" ? "" : styles.navWhileSectionOpen
            }`}
          >
            {mobileView === "list" && (
              <div className={styles.drillList}>
                {SETTINGS_GROUPS.map((g) => (
                  <div key={g.id} className={styles.drillGroup}>
                    <h2 className={styles.groupLabel}>{g.label}</h2>
                    <ul className={styles.groupItems}>
                      {SECTIONS.filter((s) => s.group === g.id).map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => select(s.id)}
                            className={styles.drillRow}
                          >
                            <span className={styles.drillLabel}>{s.label}</span>
                            <ChevronRightIcon className={styles.drillChevron} />
                          </button>
                        </li>
                      ))}
                      {g.id === "company" && (
                        <li>
                          <a
                            href={INTAKE_SETTINGS_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.drillRow}
                          >
                            <span className={styles.drillLabel}>
                              Intake settings
                            </span>
                            <ExternalLinkIcon className={styles.drillChevron} />
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
                <button type="button" onClick={logOut} className={styles.logOut}>
                  Log out
                </button>
              </div>
            )}
          </nav>

          <div
            className={`${styles.content} ${
              mobileView === "list" ? styles.contentWhileListOpen : ""
            }`}
          >
            {mobileView === "section" && (
              <button
                type="button"
                onClick={backToList}
                className={styles.backLink}
              >
                <ChevronLeftIcon className={styles.backIcon} />
                Settings
              </button>
            )}

            {active.needsSettings ? (
              settings ? (
                active.render({ settings, onPatch })
              ) : (
                <Loading />
              )
            ) : (
              active.render()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
